// Bespoke unmounted beast squads. These use one procedural group per squad member so their
// silhouettes and gait can differ completely from the shared humanoid instancing path.

import * as THREE from 'three';
import { OWNER_COLORS } from './unitStyles';

export const BESPOKE_BEAST_DEFS = new Set([
  'u-vw-razorhorn',
  'u-vw-moonfang-hunters',
  'u-vw-skybloom-drakes',
]);

function mix(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}

function mat(color: THREE.Color | number, rough = 0.9, metal = 0.02): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: color instanceof THREE.Color ? color : new THREE.Color(color),
    roughness: rough,
    metalness: metal,
    flatShading: true,
  });
}

function glow(color: number, intensity: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: intensity,
    roughness: 0.35,
  });
}

function addClovenLeg(
  root: THREE.Group,
  name: string,
  x: number,
  z: number,
  upperLen: number,
  lowerLen: number,
  hide: THREE.Material,
  hoof: THREE.Material,
): void {
  const hip = new THREE.Group();
  hip.name = name;
  hip.position.set(x, 0.9, z);

  const upper = new THREE.Mesh(new THREE.CapsuleGeometry(0.12, upperLen, 3, 6), hide);
  upper.position.y = -upperLen * 0.52;
  hip.add(upper);

  const knee = new THREE.Group();
  knee.name = `${name}Knee`;
  knee.position.y = -upperLen;
  const lower = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.065, lowerLen, 6), hide);
  lower.position.y = -lowerLen * 0.5;
  lower.rotation.x = name.includes('B') ? -0.12 : 0.08;
  knee.add(lower);
  for (const sx of [-1, 1]) {
    const toe = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.22, 5), hoof);
    toe.rotation.x = Math.PI / 2;
    toe.position.set(sx * 0.055, -lowerLen + 0.01, 0.11);
    knee.add(toe);
  }
  hip.add(knee);
  root.add(hip);
}

function addRazorPlate(parent: THREE.Object3D, x: number, y: number, z: number, size: number, armor: THREE.Material): void {
  const plate = new THREE.Mesh(new THREE.ConeGeometry(size * 0.48, size, 4), armor);
  plate.position.set(x, y, z);
  plate.rotation.set(-0.22, Math.PI / 4, 0);
  parent.add(plate);
}

function finishBeast(group: THREE.Group, uiScale: number, shadowRadius: number): THREE.Group {
  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(shadowRadius, 20),
    new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.24, depthWrite: false }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.015;
  group.add(shadow);
  group.scale.setScalar(uiScale);
  group.traverse((o) => {
    if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return group;
}

function makeMoonfang(owner: 0 | 1, uiScale: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'moonfang-werewolf';
  group.userData.beastKind = 'werewolf';
  const fur = mat(mix(0x8d9ba2, OWNER_COLORS[owner], 0.12), 0.98);
  const furDark = mat(0x384047, 0.98);
  const moon = mat(mix(0xc5d5dc, OWNER_COLORS[owner], 0.16), 0.72, 0.1);
  const claw = mat(0xe2d8c1, 0.6);
  const eye = glow(0x9fe8ff, 1.3);

  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.42, 0.82, 5, 9), fur);
  body.name = 'body';
  body.position.y = 1.55;
  body.rotation.x = 0.2;
  body.scale.set(1.08, 1, 0.9);
  group.add(body);
  const ruff = new THREE.Mesh(new THREE.IcosahedronGeometry(0.52, 1), furDark);
  ruff.position.set(0, 1.88, 0.1);
  ruff.scale.set(1.08, 0.74, 0.85);
  group.add(ruff);

  for (const [name, sx] of [['legBL', -1], ['legBR', 1]] as const) {
    const hip = new THREE.Group();
    hip.name = name;
    hip.position.set(sx * 0.25, 1.15, -0.04);
    const thigh = new THREE.Mesh(new THREE.CapsuleGeometry(0.13, 0.44, 3, 6), furDark);
    thigh.position.y = -0.25;
    hip.add(thigh);
    const knee = new THREE.Group();
    knee.name = `${name}Knee`;
    knee.position.y = -0.52;
    const shin = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.065, 0.48, 6), fur);
    shin.position.set(0, -0.22, 0.08);
    shin.rotation.x = -0.26;
    knee.add(shin);
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.11, 0.34), furDark);
    foot.position.set(0, -0.44, 0.18);
    knee.add(foot);
    for (const cx of [-0.06, 0, 0.06]) {
      const toe = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.16, 5), claw);
      toe.rotation.x = Math.PI / 2;
      toe.position.set(cx, -0.44, 0.4);
      knee.add(toe);
    }
    hip.add(knee);
    group.add(hip);
  }

  for (const [name, sx] of [['armL', -1], ['armR', 1]] as const) {
    const shoulder = new THREE.Group();
    shoulder.name = name;
    shoulder.position.set(sx * 0.48, 1.9, 0.05);
    const arm = new THREE.Mesh(new THREE.CapsuleGeometry(0.1, 0.62, 3, 6), furDark);
    arm.position.y = -0.36;
    shoulder.add(arm);
    const paw = new THREE.Mesh(new THREE.IcosahedronGeometry(0.15, 0), furDark);
    paw.position.y = -0.76;
    shoulder.add(paw);
    for (const cx of [-0.07, 0, 0.07]) {
      const talon = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.2, 5), claw);
      talon.rotation.x = Math.PI;
      talon.position.set(cx, -0.91, 0.04);
      shoulder.add(talon);
    }
    group.add(shoulder);
  }

  const head = new THREE.Group();
  head.name = 'head';
  head.position.set(0, 2.35, 0.18);
  const skull = new THREE.Mesh(new THREE.IcosahedronGeometry(0.31, 1), fur);
  skull.scale.set(0.9, 1.02, 0.95);
  head.add(skull);
  const muzzle = new THREE.Mesh(new THREE.CapsuleGeometry(0.14, 0.34, 4, 7), furDark);
  muzzle.rotation.x = Math.PI / 2;
  muzzle.position.set(0, -0.08, 0.34);
  head.add(muzzle);
  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.09, 0.3), furDark);
  jaw.name = 'jaw';
  jaw.position.set(0, -0.2, 0.34);
  head.add(jaw);
  for (const sx of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.32, 5), furDark);
    ear.position.set(sx * 0.19, 0.3, -0.02);
    ear.rotation.z = sx * -0.22;
    head.add(ear);
    const e = new THREE.Mesh(new THREE.SphereGeometry(0.045, 6, 4), eye);
    e.position.set(sx * 0.13, 0.05, 0.25);
    head.add(e);
    const fang = new THREE.Mesh(new THREE.ConeGeometry(0.022, 0.14, 5), claw);
    fang.position.set(sx * 0.07, -0.18, 0.5);
    fang.rotation.x = Math.PI;
    head.add(fang);
  }
  group.add(head);

  const harness = new THREE.Mesh(new THREE.TorusGeometry(0.41, 0.035, 5, 14), moon);
  harness.rotation.x = Math.PI / 2;
  harness.position.set(0, 1.65, 0);
  group.add(harness);
  const tail = new THREE.Group();
  tail.name = 'tail';
  tail.position.set(0, 1.45, -0.38);
  const tailMesh = new THREE.Mesh(new THREE.CapsuleGeometry(0.09, 0.62, 4, 7), furDark);
  tailMesh.rotation.x = -0.78;
  tailMesh.position.set(0, -0.16, -0.26);
  tail.add(tailMesh);
  group.add(tail);
  return finishBeast(group, uiScale, 0.72);
}

function makeSkybloomDrake(owner: 0 | 1, uiScale: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'skybloom-drake';
  group.userData.beastKind = 'drake';
  const scale = mat(mix(0xb5688f, OWNER_COLORS[owner], 0.12), 0.88, 0.05);
  const belly = mat(0xe1b3be, 0.92);
  const membrane = new THREE.MeshStandardMaterial({ color: mix(0xd98fb1, OWNER_COLORS[owner], 0.15), roughness: 0.82, side: THREE.DoubleSide, transparent: true, opacity: 0.9 });
  const horn = mat(0xe3d6b0, 0.62);
  const leaf = mat(0x7fb45a, 0.9);
  const eye = glow(0xffdf70, 1.2);

  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.42, 1.05, 5, 10), scale);
  body.name = 'body';
  body.rotation.x = Math.PI / 2;
  body.position.set(0, 1.05, -0.08);
  group.add(body);
  const breast = new THREE.Mesh(new THREE.IcosahedronGeometry(0.45, 1), belly);
  breast.position.set(0, 1.08, 0.42);
  breast.scale.set(0.85, 0.82, 1.05);
  group.add(breast);
  addClovenLeg(group, 'legFL', -0.3, 0.42, 0.4, 0.36, scale, horn);
  addClovenLeg(group, 'legFR', 0.3, 0.42, 0.4, 0.36, scale, horn);
  addClovenLeg(group, 'legBL', -0.3, -0.48, 0.4, 0.36, scale, horn);
  addClovenLeg(group, 'legBR', 0.3, -0.48, 0.4, 0.36, scale, horn);

  for (const [name, sx] of [['wingL', -1], ['wingR', 1]] as const) {
    const wing = new THREE.Group();
    wing.name = name;
    wing.position.set(sx * 0.32, 1.28, -0.05);
    const bone = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.06, 1.15, 6), horn);
    bone.rotation.z = Math.PI / 2;
    bone.position.x = sx * 0.56;
    wing.add(bone);
    const wingGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(sx * 1.12, 0, -0.12),
      new THREE.Vector3(sx * 0.66, 0, -0.82),
    ]).setIndex([0, 1, 2]);
    wingGeometry.computeVertexNormals();
    const pane = new THREE.Mesh(wingGeometry, membrane);
    wing.add(pane);
    group.add(wing);
  }

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.28, 0.66, 7), scale);
  neck.position.set(0, 1.36, 0.62);
  neck.rotation.x = -0.65;
  group.add(neck);
  const head = new THREE.Group();
  head.name = 'head';
  head.position.set(0, 1.62, 0.88);
  const skull = new THREE.Mesh(new THREE.IcosahedronGeometry(0.27, 1), scale);
  head.add(skull);
  const muzzle = new THREE.Mesh(new THREE.CapsuleGeometry(0.12, 0.36, 4, 7), belly);
  muzzle.rotation.x = Math.PI / 2;
  muzzle.position.set(0, -0.07, 0.35);
  head.add(muzzle);
  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.08, 0.3), scale);
  jaw.name = 'jaw';
  jaw.position.set(0, -0.18, 0.34);
  head.add(jaw);
  for (const sx of [-1, 1]) {
    const e = new THREE.Mesh(new THREE.SphereGeometry(0.04, 6, 4), eye);
    e.position.set(sx * 0.13, 0.05, 0.19);
    head.add(e);
    const hornMesh = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.28, 5), horn);
    hornMesh.position.set(sx * 0.13, 0.25, -0.08);
    hornMesh.rotation.x = -0.45;
    head.add(hornMesh);
  }
  // Flower crest makes the species name readable even at RTS zoom.
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2;
    const petal = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.34, 5), i % 2 ? leaf : membrane);
    petal.position.set(Math.cos(angle) * 0.16, 0.3 + Math.sin(angle) * 0.08, -0.1);
    petal.rotation.z = angle;
    head.add(petal);
  }
  group.add(head);

  const tail = new THREE.Group();
  tail.name = 'tail';
  tail.position.set(0, 1.08, -0.65);
  const tailMesh = new THREE.Mesh(new THREE.ConeGeometry(0.22, 1.45, 7), scale);
  tailMesh.rotation.x = -Math.PI / 2;
  tailMesh.position.z = -0.68;
  tail.add(tailMesh);
  group.add(tail);
  return finishBeast(group, uiScale, 0.95);
}

export function makeBeastRig(defId: string, owner: 0 | 1, uiScale = 1): THREE.Group {
  if (defId === 'u-vw-moonfang-hunters') return makeMoonfang(owner, uiScale);
  if (defId === 'u-vw-skybloom-drakes') return makeSkybloomDrake(owner, uiScale);
  if (defId !== 'u-vw-razorhorn') throw new Error(`No beast rig for ${defId}`);

  const group = new THREE.Group();
  group.name = 'razorhorn';
  group.userData.beastKind = 'razorhorn';
  const team = OWNER_COLORS[owner];
  const hide = mat(mix(0x5b3d25, team, 0.1), 0.96);
  const hideLight = mat(mix(0x8a6540, team, 0.08), 0.94);
  const dark = mat(0x281b14, 0.98);
  const bark = mat(mix(0x4b3521, team, 0.12), 0.94);
  const plate = mat(mix(0x65733b, team, 0.18), 0.82, 0.08);
  const ivory = mat(0xe2d3af, 0.62);
  const eye = glow(0xe4a33d, 1.15);

  // Long, low barrel with an oversized shoulder hump: the silhouette reads as a charging boar.
  const barrel = new THREE.Mesh(new THREE.CapsuleGeometry(0.58, 1.28, 6, 12), hide);
  barrel.name = 'body';
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 1.13, -0.05);
  barrel.scale.set(1.05, 1, 0.9);
  group.add(barrel);

  const shoulders = new THREE.Mesh(new THREE.IcosahedronGeometry(0.68, 1), hideLight);
  shoulders.position.set(0, 1.24, 0.48);
  shoulders.scale.set(1.08, 1.0, 0.86);
  group.add(shoulders);
  const haunch = new THREE.Mesh(new THREE.IcosahedronGeometry(0.52, 1), hide);
  haunch.position.set(0, 1.13, -0.65);
  haunch.scale.set(1.1, 1, 0.9);
  group.add(haunch);

  addClovenLeg(group, 'legFL', -0.42, 0.52, 0.48, 0.42, hideLight, dark);
  addClovenLeg(group, 'legFR', 0.42, 0.52, 0.48, 0.42, hideLight, dark);
  addClovenLeg(group, 'legBL', -0.4, -0.58, 0.46, 0.43, hide, dark);
  addClovenLeg(group, 'legBR', 0.4, -0.58, 0.46, 0.43, hide, dark);

  // Head is a named pivot so attacks can lower the horns and snap the jaw.
  const head = new THREE.Group();
  head.name = 'head';
  head.position.set(0, 1.3, 0.92);
  const skull = new THREE.Mesh(new THREE.IcosahedronGeometry(0.42, 1), hideLight);
  skull.scale.set(0.9, 0.78, 1.08);
  head.add(skull);
  const snout = new THREE.Mesh(new THREE.CapsuleGeometry(0.2, 0.34, 4, 8), bark);
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, -0.13, 0.43);
  snout.scale.x = 1.15;
  head.add(snout);
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.19, 8, 6), dark);
  nose.scale.set(1.2, 0.75, 0.65);
  nose.position.set(0, -0.13, 0.66);
  head.add(nose);
  for (const sx of [-1, 1]) {
    const nostril = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 4), mat(0x080706, 1));
    nostril.position.set(sx * 0.08, -0.1, 0.79);
    head.add(nostril);
    const tusk = new THREE.Mesh(new THREE.ConeGeometry(0.075, 0.5, 7), ivory);
    tusk.position.set(sx * 0.24, -0.17, 0.54);
    tusk.rotation.set(-0.82, 0, sx * -0.25);
    head.add(tusk);
    const eyeMesh = new THREE.Mesh(new THREE.SphereGeometry(0.055, 7, 5), eye);
    eyeMesh.position.set(sx * 0.27, 0.11, 0.29);
    head.add(eyeMesh);
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.3, 5), dark);
    ear.position.set(sx * 0.28, 0.33, 0.04);
    ear.rotation.z = sx * -0.58;
    head.add(ear);
  }
  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.11, 0.42), dark);
  jaw.name = 'jaw';
  jaw.position.set(0, -0.28, 0.38);
  head.add(jaw);
  group.add(head);

  // Leaf-metal razor plates run from forehead to rump, with thorny side armor around the hump.
  const ridge = [
    [0, 1.7, 0.72, 0.38],
    [0, 1.83, 0.38, 0.52],
    [0, 1.82, 0.02, 0.58],
    [0, 1.7, -0.34, 0.5],
    [0, 1.58, -0.66, 0.38],
  ] as const;
  for (const [x, y, z, size] of ridge) addRazorPlate(group, x, y, z, size, plate);
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 3; i++) {
      const thorn = new THREE.Mesh(new THREE.ConeGeometry(0.055, 0.3 - i * 0.035, 5), plate);
      thorn.position.set(sx * (0.56 - i * 0.04), 1.35 + i * 0.08, 0.45 - i * 0.35);
      thorn.rotation.z = sx * -1.15;
      group.add(thorn);
    }
  }

  // Team-colored woven harness keeps squad ownership readable without turning the hide blue/red.
  const harnessMat = mat(team, 0.72, 0.08);
  const girth = new THREE.Mesh(new THREE.TorusGeometry(0.6, 0.045, 6, 18), harnessMat);
  girth.rotation.x = Math.PI / 2;
  girth.scale.z = 0.9;
  girth.position.set(0, 1.14, 0.18);
  group.add(girth);
  for (const sx of [-1, 1]) {
    const badge = new THREE.Mesh(new THREE.OctahedronGeometry(0.12, 0), harnessMat);
    badge.position.set(sx * 0.61, 1.2, 0.18);
    group.add(badge);
  }

  const tail = new THREE.Group();
  tail.name = 'tail';
  tail.position.set(0, 1.3, -1.02);
  const tailStem = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.035, 0.56, 6), dark);
  tailStem.rotation.x = -0.9;
  tailStem.position.set(0, 0.08, -0.2);
  tail.add(tailStem);
  const tuft = new THREE.Mesh(new THREE.IcosahedronGeometry(0.13, 0), plate);
  tuft.position.set(0, 0.3, -0.4);
  tail.add(tuft);
  group.add(tail);

  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(1.05, 20),
    new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.24, depthWrite: false }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.015;
  group.add(shadow);

  group.scale.setScalar(uiScale);
  group.traverse((o) => {
    if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return group;
}

export interface BeastPose {
  phase: number;
  moveAmt: number;
  strike: number;
  charge: number;
  time: number;
}

export function poseBeastRig(group: THREE.Group, pose: BeastPose): void {
  const { phase, moveAmt, strike, charge, time } = pose;
  const kind = group.userData.beastKind as string | undefined;
  if (kind === 'werewolf') {
    const stride = Math.sin(phase) * (0.025 + moveAmt * 0.68 + charge * 0.16);
    const legL = group.getObjectByName('legBL');
    const legR = group.getObjectByName('legBR');
    const kneeL = group.getObjectByName('legBLKnee');
    const kneeR = group.getObjectByName('legBRKnee');
    if (legL) legL.rotation.x = stride;
    if (legR) legR.rotation.x = -stride;
    if (kneeL) kneeL.rotation.x = Math.max(0, Math.cos(phase)) * moveAmt * 0.5;
    if (kneeR) kneeR.rotation.x = Math.max(0, -Math.cos(phase)) * moveAmt * 0.5;
    const idleReach = Math.sin(time * 1.15) * 0.055 * (1 - moveAmt);
    const armL = group.getObjectByName('armL');
    const armR = group.getObjectByName('armR');
    if (armL) armL.rotation.x = -stride * 0.6 - strike * 1.25 + idleReach;
    if (armR) armR.rotation.x = stride * 0.6 - strike * 0.45 - idleReach;
    const head = group.getObjectByName('head');
    if (head) {
      head.rotation.x = -0.08 + charge * -0.22 + strike * 0.18 + Math.sin(time * 1.2) * 0.025;
      head.rotation.y = Math.sin(time * 0.58) * 0.16 * (1 - moveAmt);
    }
    const jaw = group.getObjectByName('jaw');
    if (jaw) jaw.rotation.x = strike * 0.5 + Math.max(0, Math.sin(time * 0.7)) * 0.04;
    const tail = group.getObjectByName('tail');
    if (tail) tail.rotation.y = Math.sin(time * (1.7 + moveAmt * 2.4) + phase * 0.2) * 0.28;
    const body = group.getObjectByName('body');
    if (body) {
      body.rotation.z = Math.sin(phase) * 0.04 * moveAmt + Math.sin(time * 0.65) * 0.018 * (1 - moveAmt);
      body.scale.y = 1 + Math.sin(time * 1.45) * 0.025 * (1 - moveAmt);
    }
    return;
  }
  if (kind === 'drake') {
    const pace = 0.02 + moveAmt * 0.5 + charge * 0.14;
    const diagonal = Math.sin(phase) * pace;
    for (const [name, swing] of [['legFL', diagonal], ['legBR', diagonal], ['legFR', -diagonal], ['legBL', -diagonal]] as const) {
      const leg = group.getObjectByName(name);
      if (leg) leg.rotation.x = swing;
    }
    const wingBeat = moveAmt > 0
      ? 0.28 + Math.sin(time * (charge ? 10 : 7)) * 0.42
      : 0.18 + Math.sin(time * 1.65) * 0.12;
    const wingL = group.getObjectByName('wingL');
    const wingR = group.getObjectByName('wingR');
    if (wingL) wingL.rotation.z = wingBeat;
    if (wingR) wingR.rotation.z = -wingBeat;
    const head = group.getObjectByName('head');
    if (head) {
      head.rotation.x = -charge * 0.2 + strike * 0.3 + Math.sin(time * 1.1) * 0.035;
      head.rotation.y = Math.sin(time * 0.48) * 0.1 * (1 - moveAmt);
    }
    const jaw = group.getObjectByName('jaw');
    if (jaw) jaw.rotation.x = strike * 0.48 + Math.max(0, Math.sin(time * 0.6)) * 0.03;
    const tail = group.getObjectByName('tail');
    if (tail) tail.rotation.y = Math.sin(time * (1.25 + moveAmt * 2.2) + phase * 0.15) * 0.34;
    const body = group.getObjectByName('body');
    if (body) {
      body.rotation.z = Math.sin(phase) * 0.035 * moveAmt;
      body.scale.x = 1 + Math.sin(time * 1.3) * 0.018 * (1 - moveAmt);
    }
    return;
  }
  const pace = 0.025 + moveAmt * 0.58 + charge * 0.24;
  const diagonal = Math.sin(phase) * pace;
  const frontLift = Math.max(0, Math.sin(phase)) * moveAmt;
  const backLift = Math.max(0, -Math.sin(phase)) * moveAmt;
  const setLeg = (name: string, swing: number, lift: number): void => {
    const leg = group.getObjectByName(name);
    const knee = group.getObjectByName(`${name}Knee`);
    if (leg) leg.rotation.x = swing;
    if (knee) knee.rotation.x = lift * 0.42;
  };
  setLeg('legFL', diagonal - strike * 0.35, frontLift);
  setLeg('legBR', diagonal, backLift);
  setLeg('legFR', -diagonal - strike * 0.35, backLift);
  setLeg('legBL', -diagonal, frontLift);

  const head = group.getObjectByName('head');
  if (head) {
    head.rotation.x = -0.08 - charge * 0.34 + strike * 0.42 + Math.sin(time * 1.5) * 0.015;
    head.rotation.y = Math.sin(time * 0.65) * 0.035 * (1 - moveAmt);
    head.position.y = 1.3 + Math.abs(Math.sin(phase)) * 0.025 * moveAmt;
  }
  const jaw = group.getObjectByName('jaw');
  if (jaw) jaw.rotation.x = strike * 0.48 + Math.max(0, Math.sin(time * 0.8)) * 0.025;
  const tail = group.getObjectByName('tail');
  if (tail) {
    tail.rotation.y = Math.sin(time * (2.4 + moveAmt * 2) + phase * 0.25) * (0.18 + moveAmt * 0.12);
    tail.rotation.x = -0.08 + Math.cos(time * 1.8) * 0.06;
  }
  const body = group.getObjectByName('body');
  if (body) body.rotation.z = Math.sin(phase) * 0.025 * moveAmt;
}
